import { configureRequestParams, getErrorMessage } from '@/utils/helpers'

import axios from './instance'

/**
 * Get the credit bank for an entity.
 * @param {string} entityId - The entity id.
 */
export const getCreditBank = async (entityId) => {
  try {
    return await axios.get(`/entities/${entityId}/credit-bank/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Recharge the credit bank for an entity.
 * @param {string} entityId - The entity id.
 * @param {object} payload - The recharge data { amount, notes }.
 */
export const rechargeCreditBank = async (entityId, payload) => {
  try {
    return await axios.post(`/entities/${entityId}/credit-bank/recharge/`, payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Adjust the credit bank balance (admin only).
 * @param {string} entityId - The entity id.
 * @param {object} payload - The adjustment data { amount, notes }.
 */
export const adjustCreditBank = async (entityId, payload) => {
  try {
    return await axios.post(`/entities/${entityId}/credit-bank/adjust/`, payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get credit transactions for an entity.
 * @param {string} entityId - The entity id.
 * @param {object} sorting - The sorting parameters.
 * @param {object} pagination - The pagination parameters.
 * @param {string} searchTerm - The search term.
 * @param {object} filters - The filter parameters.
 */
export const getCreditTransactions = async (
  entityId,
  sorting,
  pagination,
  searchTerm = null,
  filters = {},
) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`/entities/${entityId}/credit-transactions/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get a single credit transaction.
 * @param {string} entityId - The entity id.
 * @param {string} transactionId - The transaction id.
 */
export const getCreditTransaction = async (entityId, transactionId) => {
  try {
    return await axios.get(`/entities/${entityId}/credit-transactions/${transactionId}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Create a Stripe checkout session for purchasing credits.
 * @param {string} entityId - The entity id.
 * @param {number} amount - The amount in dollars to purchase.
 * @param {string} [reference=''] - Optional reference or notes for the purchase.
 */
export const createStripeCheckout = async (entityId, amount, reference = '') => {
  try {
    const payload = { amount }
    if (reference) {
      payload.reference = reference
    }
    return await axios.post(`/entities/${entityId}/credit-bank/stripe-checkout/`, payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
